import { useCallback, useRef, useState } from 'react';

import { cancelUpload, UploadOperation, UploadProgress } from '~/utils/Amplify';
import { isCustomerListFile, uploadCustomerListFile } from '~/utils/customerListUpload';

export type ComposerAttachmentStatus = 'uploading' | 'ready' | 'failed';

export interface ComposerAttachment {
  file: File;
  status: ComposerAttachmentStatus;
  progress: number;
  s3FileUri?: string;
  /** The agent's attachment id and its memory event's clientToken; kept on retry so the event is stored once. */
  idempotencyKey: string;
}

/** What the agent receives in the payload's `attachments` field. */
export interface ComposerAttachmentPayload {
  s3FileUri: string;
  fileName: string;
  idempotencyKey: string;
}

/**
 * One Customer List CSV waiting in the composer: uploaded at once through the private Customer List upload, sent only
 * with the next message. A second file replaces the pending one, cancelling its upload.
 */
export const useComposerAttachment = () => {
  const [attachment, setAttachment] = useState<ComposerAttachment | null>(null);
  const [isNotCsv, setIsNotCsv] = useState(false);
  const uploadOperationRef = useRef<UploadOperation>();
  // Bumped whenever the pending file changes, so a replaced or removed upload never writes its outcome
  const uploadIdRef = useRef(0);

  const startUpload = useCallback(async (file: File, idempotencyKey: string) => {
    uploadIdRef.current += 1;
    const uploadId = uploadIdRef.current;
    const isCurrent = () => uploadId === uploadIdRef.current;
    setAttachment({ file, status: 'uploading', progress: 0, idempotencyKey });

    const onProgress = ({ loaded, total }: UploadProgress) => {
      // An empty file reports a total of 0
      const progress = total > 0 ? Math.trunc((loaded * 100) / total) : 0;
      if (isCurrent()) setAttachment((current) => current && { ...current, progress });
    };
    try {
      const s3FileUri = await uploadCustomerListFile(file, onProgress, uploadOperationRef);
      if (isCurrent()) {
        setAttachment((current) => current && { ...current, s3FileUri, status: s3FileUri ? 'ready' : 'failed' });
      }
    } catch {
      if (isCurrent()) setAttachment((current) => current && { ...current, status: 'failed' });
    } finally {
      if (isCurrent()) uploadOperationRef.current = undefined;
    }
  }, []);

  const attach = useCallback(
    (file: File) => {
      if (!isCustomerListFile(file)) {
        setIsNotCsv(true);
        return;
      }
      setIsNotCsv(false);
      cancelUpload(uploadOperationRef);
      void startUpload(file, crypto.randomUUID());
    },
    [startUpload],
  );

  const retry = useCallback(() => {
    if (attachment) void startUpload(attachment.file, attachment.idempotencyKey);
  }, [attachment, startUpload]);

  // Removing cancels a running upload; a file already uploaded stays in the user's private prefix, unused
  const remove = useCallback(() => {
    uploadIdRef.current += 1;
    cancelUpload(uploadOperationRef);
    setAttachment(null);
    setIsNotCsv(false);
  }, []);

  const payload: ComposerAttachmentPayload | null =
    attachment?.status === 'ready' && attachment.s3FileUri
      ? { s3FileUri: attachment.s3FileUri, fileName: attachment.file.name, idempotencyKey: attachment.idempotencyKey }
      : null;

  return {
    attachment,
    isNotCsv,
    payload,
    // Send waits for the upload, and a failed one is retried or removed first
    isBlockingSend: attachment?.status === 'uploading' || attachment?.status === 'failed',
    attach,
    retry,
    remove,
  };
};
